import { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { metaFor } from "../../../shared/seo";
import { api } from "../../lib/api";
import { useMeta } from "../../lib/seo";
import { origin } from "../../lib/origin";
import { useMe } from "../../lib/useMe";
import { Spinner } from "../../components/States";

// Vendor portal shell. Client-rendered only: never server-rendered with tenant
// data, and the SSR loader returns 200 + noindex for this prefix.

export function Dashboard() {
  const { me, loading } = useMe();
  useMeta({ ...metaFor({ route: "notfound", base: origin() }), title: "Dashboard · CryptoChit" });

  if (loading) return <Spinner />;
  if (!me?.user) {
    return (
      <div className="empty-state pad-empty">
        <h1>Sign in to manage your listing</h1>
        <p>The vendor portal is for people who have claimed a profile.</p>
        <Link to="/" className="btn btn-ghost">Back home</Link>
      </div>
    );
  }

  return (
    <div>
      <h1>Dashboard</h1>
      <nav className="pill-group u-49f14f8">
        <NavLink to="/dashboard" end>Listings</NavLink>
        <NavLink to="/dashboard/submit">Add a listing</NavLink>
        <NavLink to="/dashboard/claim">Claim a profile</NavLink>
        <NavLink to="/dashboard/reviews">Reviews</NavLink>
        <NavLink to="/dashboard/analytics">Analytics</NavLink>
        {/* Team is hidden for now. The route and its page are untouched, so
            /dashboard/team still works for anyone holding the link and nothing
            about membership or invitations has been disabled — only the tab is
            gone. Restore by putting this link back. */}
        <NavLink to="/dashboard/developer">Developer</NavLink>
      </nav>
      <Outlet />
    </div>
  );
}

export function Listings() {
  const [items, setItems] = useState<Awaited<ReturnType<typeof api.dashEntities>>["items"] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [desc, setDesc] = useState("");
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    api.dashEntities().then((r) => setItems(r.items)).catch(() => setItems([]));
  }, []);

  if (!items) return <Spinner />;
  if (items.length === 0) {
    return (
      <div className="empty-state pad-empty">
        <h2>No listings yet</h2>
        <p>
          Claim a profile that is already in the directory, or add one that is missing.
        </p>
        <div className="row gap-8 u-center">
          <Link to="/dashboard/claim" className="btn btn-primary">Claim a profile</Link>
          <Link to="/dashboard/submit" className="btn btn-ghost">Add a listing</Link>
        </div>
      </div>
    );
  }

  const save = async (id: string) => {
    await api.dashUpdateEntity(id, { short_desc: desc });
    setSaved(id);
    setEditing(null);
    setItems((prev) => prev?.map((i) => (i.id === id ? { ...i, short_desc: desc } : i)) ?? prev);
  };

  return (
    <div className="grid gap-12">
      {items.map((e) => (
        <article key={e.id} className="card p-16">
          <div className="row between">
            <Link to={`/${e.type}/${e.slug}`}><strong>{e.name}</strong></Link>
            <span className="muted u-474a305">
              {e.review_count} reviews
            </span>
          </div>
          {editing === e.id ? (
            <div className="row gap-8 mt-10">
              <input
                className="input flex-1"
                value={desc}
                onChange={(ev) => setDesc(ev.target.value)}
                placeholder="Short description"
              />
              <button className="btn btn-primary btn-sm" onClick={() => void save(e.id)}>Save</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          ) : (
            <div className="row gap-8 mt-10">
              <p className="muted u-7b31594">{e.short_desc ?? "No description yet."}</p>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => { setEditing(e.id); setDesc(e.short_desc ?? ""); }}
              >
                Edit
              </button>
            </div>
          )}
          {saved === e.id && <p className="muted u-474a305">Saved.</p>}
        </article>
      ))}
    </div>
  );
}
